export default function AppPrivacy() {
  return (
    <div
      style={{
        height: '100vh',
        overflowY: 'auto',
        WebkitOverflowScrolling: 'touch',
        background: '#030a0f',
      }}
    >
    <div
      style={{
        maxWidth: 720,
        margin: '0 auto',
        padding: '48px 24px 80px',
        fontFamily:
          "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif",
        fontSize: 16,
        lineHeight: 1.7,
        color: '#d1d5db',
      }}
    >
      <h1 style={{ color: '#fff', fontSize: 28, fontWeight: 700, marginBottom: 8 }}>
        Privacy Policy
      </h1>
      <p style={{ color: '#6b7280', fontSize: 14, marginBottom: 40 }}>
        Operator App &mdash; Last updated: February 18, 2026
      </p>

      <div
        style={{
          background: '#0f1a24',
          border: '1px solid #1f2937',
          borderRadius: 8,
          padding: '16px 20px',
          marginBottom: 16,
        }}
      >
        <p style={{ margin: 0 }}>
          Operator is a companion mobile app for the Matrix sports photography platform. It is
          developed by Parallax Pictures (&ldquo;we&rdquo;, &ldquo;us&rdquo;, &ldquo;our&rdquo;).
          This policy explains what data the app collects, how it is used, and your rights regarding
          that data.
        </p>
      </div>

      <Section title="1. Overview">
        <p>
          Operator connects to the Matrix server to provide mobile access to photo management,
          editing, and workflow tools. The app is designed for professional photographers and editors
          working within their own profile.
        </p>
        <p>
          We built Operator with a privacy-first approach. The app does not collect personal data
          beyond what is strictly necessary to function, does not use advertising or analytics, and
          does not track you.
        </p>
      </Section>

      <Section title="2. Information We Collect">
        <h3 style={styles.h3}>2.1 Account Credentials</h3>
        <p>
          To use the app, you enter a username and password to authenticate with your Matrix Operator
          server. These credentials are transmitted directly to your server over HTTPS. We (Parallax
          Pictures) never receive, store, or have access to your credentials.
        </p>

        <h3 style={styles.h3}>2.2 Data Stored on Your Device</h3>
        <p>The app stores a small amount of data locally on your device:</p>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Data</th>
              <th style={styles.th}>Storage Location</th>
              <th style={styles.th}>Purpose</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td style={styles.td}>Authentication token</td>
              <td style={styles.td}>iOS Keychain / Android Keystore</td>
              <td style={styles.td}>Maintain your login session</td>
            </tr>
            <tr>
              <td style={styles.td}>Server URL</td>
              <td style={styles.td}>AsyncStorage</td>
              <td style={styles.td}>Connect to your Matrix server</td>
            </tr>
            <tr>
              <td style={styles.td}>User preferences</td>
              <td style={styles.td}>AsyncStorage</td>
              <td style={styles.td}>Remember your display settings</td>
            </tr>
          </tbody>
        </table>
        <p>
          This data remains on your device and is not transmitted to Parallax Pictures or any third
          party. You can clear it at any time by logging out or uninstalling the app.
        </p>

      </Section>

      <Section title="3. Information We Do Not Collect">
        <p>
          For clarity, the Operator app does <strong>not</strong>:
        </p>
        <ul style={styles.ul}>
          <li>Use analytics, advertising, or behavioral tracking of any kind</li>
          <li>Use cookies or tracking pixels</li>
          <li>Access your camera, microphone, location, contacts, or other device sensors</li>
          <li>Collect your IP address, device identifiers, or browsing history</li>
          <li>Create user profiles or fingerprints</li>
          <li>Sell, rent, or share personal data for marketing purposes</li>
        </ul>
      </Section>

      <Section title="4. Your Matrix Server Data">
        <p>
          All content you interact with in the app -- including photos, person records, team data,
          and captions -- is stored on and served by the Matrix Operator server. Parallax Pictures
          does not have access to this data. The privacy practices governing that data are determined
          by your organization&apos;s own policies and the Matrix platform&apos;s privacy policy.
        </p>

        <h3 style={styles.h3}>4.1 Face Data and Biometric Processing</h3>
        <p>
          The Operator app does not collect, process, or store face data or biometric information
          from your device. The app does not access your device camera, photo library, or biometric
          sensors (such as Face ID).
        </p>
        <p>
          The Matrix server platform processes face data exclusively on the server side as part of
          its editorial photo management workflow. When accredited professional sports photographers
          upload images to the platform, the server performs face detection and generates facial
          embeddings to identify publicly known persons (athletes, coaches, officials) for editorial
          captioning and keywording purposes. This face data:
        </p>
        <ul style={styles.ul}>
          <li>
            Is derived solely from photos uploaded by accredited press photographers, not from app
            users
          </li>
          <li>
            Is processed and stored entirely on the server infrastructure, never on your device
          </li>
          <li>
            Is used exclusively for editorial person identification and label propagation within the
            platform
          </li>
          <li>Is never shared with third parties</li>
          <li>Is never used for surveillance, advertising, or user profiling</li>
          <li>
            Is retained for as long as the associated photos remain in the system; when a photo is
            deleted, all related face detection data and embeddings are removed
          </li>
        </ul>
        <p>
          The retention and management of server-side face data is determined by the customer
          organization&apos;s own data management policies. Customers can delete any photo and its
          associated face data at any time.
        </p>
      </Section>

      <Section title="5. Third-Party Services">
        <p>
          The app does not integrate any third-party services. No advertising networks, social media
          SDKs, analytics platforms, or other third-party code receives data from the app.
        </p>
      </Section>

      <Section title="6. Data Retention">
        <p>
          Locally stored data (auth token, server URL, preferences) persists until you log out or
          uninstall the app.
        </p>
      </Section>

      <Section title="7. Data Security">
        <p>
          We take reasonable measures to protect the limited data involved in the app&apos;s
          operation:
        </p>
        <ul style={styles.ul}>
          <li>
            Authentication tokens are stored in the platform&apos;s secure enclave (iOS Keychain /
            Android Keystore), which provides hardware-backed encryption
          </li>
          <li>
            All network communication with your Matrix server uses HTTPS (TLS 1.2 or later)
          </li>
          <li>No sensitive data is written to application logs</li>
        </ul>
      </Section>

      <Section title="8. Children's Privacy">
        <p>
          Operator is a business tool for professional sports photography workflows. It is not
          directed at children under 16. We do not knowingly collect personal data from children. If
          you believe a child has provided personal data through the app, please contact us and we
          will delete it promptly.
        </p>
      </Section>

      <Section title="9. Your Rights">
        <p>
          Depending on your jurisdiction, you may have the following rights regarding your personal
          data:
        </p>
        <ul style={styles.ul}>
          <li>
            <strong>Access</strong> -- Request a copy of any personal data we hold about you
          </li>
          <li>
            <strong>Deletion</strong> -- Request deletion of your personal data
          </li>
          <li>
            <strong>Correction</strong> -- Request correction of inaccurate data
          </li>
          <li>
            <strong>Portability</strong> -- Receive your data in a machine-readable format
          </li>
          <li>
            <strong>Objection</strong> -- Object to processing of your data
          </li>
          <li>
            <strong>Restriction</strong> -- Request restriction of processing
          </li>
        </ul>
        <p>
          Because the app collects minimal data, most of these rights will be straightforward to
          fulfill. For any request, contact us at the address below.
        </p>
        <p>
          For data stored on your organization&apos;s Matrix server, contact your
          organization&apos;s administrator, as they are the data controller for that information.
        </p>

        <h3 style={styles.h3}>For California Residents (CCPA/CPRA)</h3>
        <p>
          We do not sell or share your personal information as defined by the California Consumer
          Privacy Act. You have the right to know what personal information is collected and to
          request its deletion. Given our minimal data collection, exercising these rights can be
          done by contacting us directly.
        </p>

        <h3 style={styles.h3}>For EU/EEA Residents (GDPR)</h3>
        <p>
          The legal basis for processing data is our legitimate interest in providing and maintaining
          the app (Article 6(1)(f) GDPR). You have the right to lodge a complaint with your local
          data protection supervisory authority if you believe your rights have been violated.
        </p>
      </Section>

      <Section title="10. Changes to This Policy">
        <p>
          We may update this Privacy Policy from time to time. When we make material changes, we will
          update the &ldquo;Last updated&rdquo; date at the top of this page. We encourage you to
          review this policy periodically. Continued use of the app after changes constitutes
          acceptance of the updated policy.
        </p>
      </Section>

      <Section title="11. Contact">
        <p>
          If you have questions about this Privacy Policy or wish to exercise your data rights,
          contact us at:
        </p>
        <p>
          <strong>Parallax Pictures</strong>
          <br />
          Email:{' '}
          <a href="mailto:info@parallaxpictures.nl" style={styles.link}>
            info@parallaxpictures.nl
          </a>
        </p>
      </Section>

      <div
        style={{
          marginTop: 48,
          paddingTop: 24,
          borderTop: '1px solid #1f2937',
          color: '#6b7280',
          fontSize: 13,
        }}
      >
        <p>&copy; 2026 Parallax Pictures. All rights reserved.</p>
        <p>
          This privacy policy applies to the Operator mobile app (iOS: com.parallax.operator.app,
          Android: com.parallax.operator.app).
        </p>
      </div>
    </div>
    </div>
  );
}

function Section({ title, children }) {
  return (
    <>
      <h2
        style={{
          color: '#fff',
          fontSize: 20,
          fontWeight: 600,
          marginTop: 40,
          marginBottom: 16,
          paddingBottom: 8,
          borderBottom: '1px solid #1f2937',
        }}
      >
        {title}
      </h2>
      {children}
    </>
  );
}

const styles = {
  h3: {
    color: '#e5e7eb',
    fontSize: 16,
    fontWeight: 600,
    marginTop: 24,
    marginBottom: 8,
  },
  table: {
    width: '100%',
    borderCollapse: 'collapse',
    marginBottom: 16,
    fontSize: 14,
  },
  th: {
    textAlign: 'left',
    padding: '10px 12px',
    borderBottom: '1px solid #1f2937',
    color: '#9ca3af',
    fontWeight: 600,
    fontSize: 12,
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
  },
  td: {
    textAlign: 'left',
    padding: '10px 12px',
    borderBottom: '1px solid #1f2937',
    color: '#d1d5db',
  },
  ul: {
    marginBottom: 14,
    paddingLeft: 24,
  },
  link: {
    color: '#60a5fa',
    textDecoration: 'none',
  },
};
